import { useState } from "react";
import { IOrbProps } from "./App";
import { useApi } from './components/useApi';
import { useUserContext } from './components/useUserContext';
import { useDataContext } from './components/useDataContext';
import { useIntegration } from './components/useIntegration';
import React from "react";
import { HashRouter, Switch, Route } from 'react-router-dom';
import { ErrorBoundary } from "./components/ErrorBoundary";
import { IntegrationContext } from "./components/IntegrationContext";
import { DataContext } from "./components/DataContext";
import { OrbUserContext } from "./components/OrbUserContext";
import { CrLoadingOverlay } from './components/cr/CrLoadingOverlay';
import { AppGlobals, AppIDs } from "./AppGlobals";
import { withErrorHandling } from './components/withErrorHandling';
import { NotAuthorised, NotFound } from "./Routes";
import LoadingWhite from "./LoadingWhite";
import { CLMenu } from "./components/CLMenu";
import ClUpdates from "./components/CL/ClUpdates";
import ClProperties from "./components/CL/ClProperties";
import ClReport from "./components/CL/ClReport";
import { ClWelcome } from "./components/CL/ClWelcome";
import { Insights } from "./components/Insights";
import { TopBanner } from "./TopBanner";
import { MenuToggle } from "./components/MenuToggle";
import { buildCLAppBreadcrumb } from "./breadcrumbs";
import { useHistory, useLocation } from "react-router-dom";
import { IEntityDescription1 } from "./types";

const CLApp: React.FunctionComponent<IOrbProps> = (props: IOrbProps) => {

    const history = useHistory();
    const location = useLocation();
    const [entityDescriptionForMenu, setEntityDescriptionForMenu] = useState<IEntityDescription1>();

    const { dataServices, errorHandling } = props;
    const apiConnected = useApi(dataServices);
    const uc = useUserContext(apiConnected, dataServices, errorHandling);
    const ld = useDataContext(apiConnected, dataServices, errorHandling);

    const integration = useIntegration({
        disableDirectorateManagement: false,
        disableGroupManagement: false,
        disablePartnerOrganisationManagement: false,
        disableProjectManagement: false,
        disableUserManagement: false,
        dataSourceName: ''
    });

    const routeProps = {
        ...props,
        apiConnected,
        entityDescriptionForMenu: entityDescriptionForMenu,
        onEntityDescriptionChange: setEntityDescriptionForMenu,
        ...uc,
        ...ld
    };

    const [isNavigationVisible, setNavigationVisible] = useState(false);
    const toggleNavigation = () => setNavigationVisible(!isNavigationVisible);

    if (uc.userContext.UserId === null) return (<LoadingWhite />);

    const appName = props.menuData.filter(m => m.ID === AppIDs.CL)[0].AppName;

    const breadcrumbItems = buildCLAppBreadcrumb({
        customerTitle: AppGlobals.CustomerTitle,
        appName,
        location,
        history
    });

    return (
        <React.Fragment>
            <ErrorBoundary>
                <IntegrationContext.Provider value={integration}>
                    <DataContext.Provider value={ld}>
                        <OrbUserContext.Provider value={uc}>

                            <div className="main">
                                <div className="container">

                                    {/* LEFT NAVIGATION (drawer) */}
                                    <div className={`navigation ${isNavigationVisible ? 'show' : ''}`}>
                                        {!uc.userContext.UserId
                                            ? <CrLoadingOverlay isLoading={true} />
                                            : <CLMenu {...routeProps} onLinkClicked={toggleNavigation} />
                                        }
                                    </div>

                                    {/* BACKDROP */}
                                    <div
                                        className={`nav-backdrop ${isNavigationVisible ? 'show' : ''}`}
                                        onClick={toggleNavigation}
                                        aria-hidden={!isNavigationVisible}
                                    />

                                    {/* RIGHT CONTENT AREA */}
                                    <div className="right-area">
                                        <div className="top-banner">
                                            <MenuToggle onToggleNavigation={toggleNavigation} />

                                            <TopBanner
                                                customerTitle={AppGlobals.CustomerTitle}
                                                userName={AppGlobals.UserTitle}
                                                breadcrumbItems={breadcrumbItems}
                                                onLogout={props.onLogout}
                                            />
                                        </div>

                                        <div className="content">
                                            <HashRouter>
                                                <Switch>
                                                    <Route exact path="/a6/welcome" {...routeProps}>
                                                        <ClWelcome />
                                                    </Route>
                                                    <Route exact path="/a6/insights" {...routeProps}>
                                                        <Insights dashboardRef='ContingentLabourDashboard' />
                                                    </Route>
                                                    <Route path="/a6/manage-cases" {...routeProps}>
                                                        <ClUpdates {...routeProps} />
                                                    </Route>
                                                    <Route path="/a6/outputs" {...routeProps}>
                                                        <ClReport {...routeProps} />
                                                    </Route>
                                                    <Route path="/a6/properties" {...routeProps}>
                                                        <ClProperties {...routeProps} />
                                                    </Route>
                                                    <Route path="/not-authorised">
                                                        <NotAuthorised />
                                                    </Route>
                                                    <Route path="*">
                                                        <NotFound />
                                                    </Route>
                                                </Switch>
                                            </HashRouter>
                                        </div>
                                    </div>

                                </div>
                            </div>

                        </OrbUserContext.Provider>
                    </DataContext.Provider>
                </IntegrationContext.Provider>
            </ErrorBoundary>
        </React.Fragment>
    );
};

export const CLAppWithErrorHandling = withErrorHandling(CLApp);
